import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { setAuthHeader } from "./api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    // If returning from Emergent OAuth callback, skip /me — AuthCallback handles it
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refreshMe().finally(() => setLoading(false));
  }, [refreshMe]);

  const login = async (email, password, remember_me = false) => {
    const { data } = await api.post("/auth/login", { email, password, remember_me });
    if (data.token) setAuthHeader(data.token, { persist: !!remember_me });
    setUser(data.user);
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    if (data.token) setAuthHeader(data.token, { persist: !!payload?.remember_me });
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    setAuthHeader(null);
    setUser(null);
  };

  const exchangeEmergentSession = async (session_id, role) => {
    // Read the "Remember me" choice the user made on the login page before
    // redirect (Login.jsx writes it into sessionStorage below).
    const remember_me =
      typeof window !== "undefined" &&
      sessionStorage.getItem("venturaz_pending_remember_me") === "1";
    sessionStorage.removeItem("venturaz_pending_remember_me");
    const body = { session_id, remember_me };
    if (role) body.role = role;
    const { data } = await api.post("/auth/emergent/callback", body);
    if (data.token) setAuthHeader(data.token, { persist: remember_me });
    setUser(data.user);
    return data.user;
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refreshMe, exchangeEmergentSession, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
